<template>
<div :class="`app ${shoplineMode ? 'shopline-mode' : ''}`">
  <template v-if="state === 'token'">
    <div class="validating" key="validating-token">
      <i class="mdi mdi-loading mdi-spin" />
      {{ $t('login.validating') }}
    </div>
  </template>
  <template v-else-if="state === 'done'">
    <template v-if="user">
      <Sidebar />
      <Uploader />
      <ChooseCountry />
    </template>
    <transition name="toggle-route">
      <router-view />
    </transition>
  </template>
  <portal-target name="modals" multiple />
</div>
</template>

<script>

import moment from 'moment';
import Sidebar from '@/partials/sidebar.vue';
import ChooseCountry from '@/components/ChooseCountry.vue';
import Uploader from '@/components/uploader.vue';

import { getQueryParameterValue } from '@/utils';

const SESSION_TIMER = 60000;
const ACCOUNT_TIMER = 5000;

export default {
  components: {
    Sidebar,
    ChooseCountry,
    Uploader,
  },
  computed: {
    /**
     * Returns the current token if there is one
     * @returns {?String}
     */
    token() {
      return this.$store.getters['User/token'];
    },
    /**
     * Return a boolean if there is a user aka logged in
     * @return {Boolean}
     */
    loggedIn() {
      return !!this.$store.getters['User/user'];
    },
    accountStatus() { return this.$store.getters['AccountManager/accountStatus']; },
    payoutsDisabled() { return this.$store.getters['AccountManager/payoutsDisabled']; },
    bankAccountNeeded() { return this.$store.getters['AccountManager/bankAccountNeeded']; },
    accountCountry() { return this.$store.getters['AccountManager/accountCountry']; },
    reqDeadline() { return this.$store.getters['AccountManager/reqDeadline']; },
    shoplineMode() { return this.$store.getters['Theme/shoplineMode']; },
  },
  methods: {
    /**
     * Notifications
     * Go through some data and print out some notifications if needed
     */
    async notifications() {
      const {
        accountStatusState,
        payoutsDisabled,
        bankAccountNeeded,
        accountCountry,
        featuresEnabled,
        account,
      } = this;

      if (accountCountry && (accountCountry.toLowerCase() === 'se' || accountCountry === '') && account.verification.enabled === false) {
        return;
      }

      if (!featuresEnabled.includes('ppv')) {
        return;
      }

      if (accountStatusState.state === 'incomplete') {
        this.$store.dispatch('Notifications/add', {
          message: `<strong>${this.$t('dashboard.update_stripe')}.</strong> ${this.$t('dashboard.update_stripe_description')}`,
          button: {
            icon: 'warning',
            class: 'small-white',
            text: this.$t('dashboard.update_stripe'),
            func: () => {
              this.$router.push('/settings/payout');
            },
          },
          state: 'warning',
          type: 'top-bar',
        });
        return;
      }

      if (accountStatusState.state === 'pending') {
        this.$store.dispatch('Notifications/add', {
          message: this.$t(accountStatusState.message),
          clearable: true,
          state: 'pending',
          type: 'top-bar',
        });
      }

      if (accountStatusState.type !== 'success' && accountStatusState.type !== '') {
        this.$store.dispatch('Notifications/add', {
          message: this.$t(accountStatusState.message),
          button: {
            icon: 'warning',
            class: 'small-white',
            text: this.$t('dashboard.update_account'),
            func: () => {
              this.$router.push('/settings/payout');
            },
          },
          state: accountStatusState.type,
          type: 'top-bar',
        });
      }

      if (payoutsDisabled && bankAccountNeeded) {
        this.$store.dispatch('Notifications/add', {
          message: `<strong>${this.$t('dashboard.payouts_disabled')}.</strong> ${this.$t('dashboard.add_bank_payouts')}`,
          button: {
            icon: 'warning',
            class: 'small-white',
            text: this.$t('dashboard.add_bank_account'),
            func: () => {
              this.$router.push('/settings/payout');
            },
          },
          state: 'warning',
          type: 'top-bar',
        });
      }
    },
    /**
     * Starts a timer that runs each minute to check if user is still logged in
     */
    async sessionTimer() {
      clearTimeout(this.timer.session);

      const { status } = await this.$store.dispatch('User/verify');
      if (typeof status !== 'undefined') {
        if (status === 401) {
          this.$store.dispatch('User/unsetUser');
          return;
        }

        this.$store.dispatch('Notifications/add', {
          message: `<strong>${this.$t('errors.connectivity_problem')}</strong> ${this.$t('errors.please_check_internet_connection')}`,
          button: {
            icon: 'warning',
            class: 'small-white',
            text: this.$t('common.reload_page'),
            func: () => {
              window.location.reload();
            },
          },
          clearable: true,
          state: 'pending',
          type: 'top-bar',
        });
      }

      this.timer.session = setTimeout(this.sessionTimer, SESSION_TIMER);
    },
    /**
     * Starts a timer that runt each 5 seconds when the account is pendning
     */
    async accountTimer() {
      clearTimeout(this.timer.account);
      await this.$store.dispatch('AccountManager/getAccount');
      this.timer.account = setTimeout(this.accountTimer, ACCOUNT_TIMER);
    },
    /**
     * Validate token
     * @param {String} token
     */
    async validateToken(token) {
      window.auth = token;
      const user = await this.$store.dispatch('User/getUser');

      if (user) {
        if ([1, 6, 15, 16].indexOf(user.user.permission_id) >= 0) {
          await this.$store.dispatch('AccountManager/getAccountSettings');
          await this.$store.dispatch('AccountManager/getAccount');
          await this.$store.dispatch('AccountManager/getUsers');
          await this.$store.dispatch('AccountManager/getShoppingSettings');
        }

        this.sessionTimer();
      } else {
        const urlParams = new URLSearchParams(window.location.search);
        const numParams = Array.from(urlParams.entries()).length;
        const redirect = encodeURIComponent(`${window.location.pathname}${numParams > 0 ? `?${urlParams.toString()}` : ''}`);
        delete window.assumeParam;
        this.$store.commit('User/setAssumeParam', false);
        this.$store.commit('Notifications/clearAll');
        this.$store.dispatch('User/logout', true);
        this.$router.push(`/login?redirect=${redirect}`);
      }

      this.state = 'done';
    },
  },
  watch: {
    assumed(newVal, oldVal) {
      if (oldVal === false) {
        return;
      }
      this.$router.go();
    },
    assumeParam(newVal) {
      if (newVal === false) {
        const { query } = this.$route;
        delete query.assumeParam;

        this.$router.replace({
          ...query,
        }).catch(() => {});

        this.$router.go();
      }
    },
    /**
     * Listen for routechanges and send to GA
     */
    $route(a, b) {
      this.$store.dispatch('Theme/lastRoute', b);
      this.$googleanalytics.track();
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });

      if (this.$store.getters['User/assumeParam']) {
        this.$router.replace({
          query: {
            ...this.$route.query,
            assume: window.assumeParam,
          },
        }).catch(() => {});
      }
    },
    /**
     * Watch for changes to loggedIn computed variable
     * @param {Boolean} bool
     */
    async loggedIn(bool) {
      if (!bool) {
        clearTimeout(this.timer.session);
        clearTimeout(this.timer.account);
      }

      if (bool) {
        await this.$store.dispatch('Play/getLVSPConfig');
      }
    },
    /**
     * Watch for changes to accountStatus computed variable
     */
    accountStatus: {
      immediate: true,
      handler(status) {
        if (status === 'pending') {
          this.accountTimer();
        } else {
          clearTimeout(this.timer.account);
        }

        this.notifications();
      },
    },
    accountSettings: {
      immediate: true,
      handler(settings) {
        if (settings) {
          if (settings.shopping?.enabled && settings.subscription?.trial) {
            const trialStart = settings.stripe_plan?.trial_starts_at;
            const trialEnd = settings.stripe_plan?.trial_ends_at;

            const totalPeriod = moment(new Date(trialEnd)).diff(new Date(trialStart), 'days');
            const totalLeft = moment(new Date(trialEnd)).diff(new Date(), 'days');
            const totalUsed = totalPeriod - totalLeft;

            this.$store.dispatch('Notifications/add', {
              message: totalLeft <= 7 ? `${totalLeft} days remaining on trial period ` : 'Trial period',
              secondaryMessage: totalUsed <= 23 ? `${totalUsed}/${totalPeriod}` : null,
              button: {
                icon: 'check-circle-24',
                class: 'small-white',
                text: 'Upgrade package',
                func: () => {
                  this.$router.push('/settings/plan');
                },
              },
              state: '',
              type: totalUsed < 23 ? 'menu-bar' : 'top-bar',
            });
          }

          if (this.$store.getters['AccountManager/trialEnded']) {
            this.$store.dispatch('Notifications/add', {
              message: 'Trial period has ended',
              button: {
                icon: 'check-circle-24',
                class: 'small-white',
                text: 'Upgrade package',
                func: () => {
                  this.$router.push('/settings/plan');
                },
              },
              state: 'warning',
              type: 'top-bar',
            });
          }
          if (this.$store.getters['AccountManager/subscriptionEnded']) {
            this.$store.dispatch('Notifications/add', {
              message: 'Your subscription has ended',
              button: {
                icon: 'check-circle-24',
                class: 'small-white',
                text: 'Upgrade package',
                func: () => {
                  this.$router.push('/settings/plan');
                },
              },
              state: 'warning',
              type: 'top-bar',
            });
          }
        } else {
          this.$store.commit('Notifications/clearAll');
        }
      },
    },
  },
  data() {
    return {
      state: '',
      timer: {
        session: null,
      },
    };
  },
  async mounted() {
    const forceLocale = getQueryParameterValue('lang');
    if (['en', 'sv'].indexOf(forceLocale) >= 0) {
      this.$i18n.locale = forceLocale;
    }

    const assume = getQueryParameterValue('assume');
    if (assume) {
      window.assumeParam = assume;
      this.$store.commit('User/setAssumeParam', assume);
    }

    const red = getQueryParameterValue('redirect');
    const webtoken = getQueryParameterValue('token', red ?? '');
    if (webtoken) {
      await this.$store.commit('User/setToken', webtoken);
      await this.validateToken(webtoken);
      this.$router.push(red);
    } else {
      const { token } = this;
      if (token) {
        this.validateToken(token);
      } else {
        this.state = 'done';
      }
    }
  },
};
</script>

<style lang="scss">

$mdi-font-path: "~@mdi/font/fonts";

@import url('https://fonts.googleapis.com/css2?family=Inter:wght@100;300;400;500;600&family=Fira+Mono&display=swap');
@import '~@mdi/font/scss/materialdesignicons.scss';
@import '~video.js/dist/video-js.css';
@import 'styles/variables.scss';
@import 'styles/fonts.scss';
@import 'styles/utils.scss';
@import 'styles/components/broadcast-app-item.scss';
@import './variables.sass';
@import './transitions.sass';
@import './base.sass';
@import './utilities.sass';
@import './styles/components/library-list-item.sass';
@import './styles/components/modal-window.scss';
@import './styles/components/access-page';

body {
  -webkit-overflow-scrolling: touch;
  scrollbar-color: #1643c9;
  scrollbar-width: thin;

  &::-webkit-scrollbar-track {
    background-color: #f1f3fa;
  }

  &::-webkit-scrollbar {
    height: 4px;
    width: 4px;
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background-color: #1643c9;
  }
}

.shopline-mode {
  nav.breadcrumbs:empty {
    height: 24px;
  }
  aside.sidebar {
    & > header {
      & > svg { display: none; }
      @media (min-width: 1024px) {
        display: none;
      }
    }
    .menu-item.icon-video { display: none; }
    nav:last-child > a { display: none !important; }
  }

  .onboarding {
    display: none;
  }

  .info-card:has(+ .onboarding) {
    margin-right: 0 !important;
  }
}
</style>
